import { ChartData } from 'chart.js';
import {
    BoletaRecibidaDto,
    BoletaTerceroEmitidaDto,
    DocumentoRegistroComprasVentasDto,
    MultaDto,
    ReporteBoletasHonorariosAnualDto,
    ReporteBoletasRecibidasAnualDto,
    ReporteBoletasTercerosEmitidasAnualDto,
    ReporteComunicacionesDto,
    ReporteDatosTributariosDto,
    ReporteDeudasPropiedadesDto,
    ReporteF29Dto,
    ReporteF50Dto,
    ReporteMultasDto,
    ReporteRegistroComprasVentasDto,
    ResumenMensualBteEmitidasDto,
    ResumenMensualBoletasRecibidasDto,
    ResumenPeriodoHonorarioDto,
    ReporteDevolucionesIvaDto,
    ReporteF22Dto
} from '../reportes-api.services';
import {
    DocumentoRegistroComprasVentasParseado,
    MesBoletasHonorariosParseado,
    ReporteBoletasHonorariosParseado,
    ReporteComunicacionesParseado,
    ReporteDinamicoParseado,
    ReporteF50MesParseado,
    ReporteF50yF29Parseado,
    MultaParseada,
    ReporteMultasParseado,
    ReporteRegistroComprasVentasParseado
} from './tipos-reporte';
import { ColumnaReporte as Columnas, Tabla } from '../core/reporte';

interface RespuestasBoletasHonorarios {
    recibidas: ReporteBoletasRecibidasAnualDto | null;
    emitidas: ReporteBoletasHonorariosAnualDto | null;
    terceros: ReporteBoletasTercerosEmitidasAnualDto | null;
}

export function nombreMes(mes: number): string {
    return ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'][mes - 1] ?? `Mes ${mes}`;
}

export function parsearBoletasHonorarios(respuestas: RespuestasBoletasHonorarios, anioSeleccionado: number): ReporteBoletasHonorariosParseado {
    const { recibidas, emitidas, terceros } = respuestas;

    return {
        rut: emitidas?.rut || recibidas?.rut || terceros?.rut || '',
        contribuyente: emitidas?.contribuyente || recibidas?.contribuyente || terceros?.contribuyente || null,
        anio: emitidas?.anio || recibidas?.anio || terceros?.anio || anioSeleccionado,
        generadoEn: emitidas?.generadoEn || recibidas?.generadoEn || terceros?.generadoEn || '',
        recibidas: normalizarMesesBoletasConDetalle(recibidas, anioSeleccionado),
        emitidas: normalizarMesesBoletas(emitidas, anioSeleccionado),
        terceros: normalizarMesesBoletasConDetalle(terceros, anioSeleccionado)
    };
}

function normalizarMesesBoletas(reporte: ReporteBoletasHonorariosAnualDto | null, anioSeleccionado: number): MesBoletasHonorariosParseado[] {
    const resumenPorMes = new Map((reporte?.meses ?? []).map((mes) => [mes.mes.trim().toUpperCase(), mes]));

    return Array.from({ length: 12 }, (_, indice) => {
        const numeroMes = indice + 1;
        const mes = nombreMes(numeroMes);
        const resumen = resumenPorMes.get(mes.toUpperCase());

        return crearMesBoletas(numeroMes, mes, reporte?.anio ?? anioSeleccionado, reporte?.generadoEn ?? '', resumen);
    });
}

function crearMesBoletas(id: number, mes: string, anio: number, generadoEn: string, resumen?: ResumenPeriodoHonorarioDto): MesBoletasHonorariosParseado {
    return {
        id,
        mes,
        anio,
        cantidadBoletas: (resumen?.emisionesVigentes ?? 0) + (resumen?.emisionesAnuladas ?? 0),
        vigentes: resumen?.emisionesVigentes ?? 0,
        anuladas: resumen?.emisionesAnuladas ?? 0,
        bruto: resumen?.honorarioBruto ?? 0,
        retenido: (resumen?.retencionTerceros ?? 0) + (resumen?.retencionContribuyente ?? 0),
        pagado: resumen?.totalLiquido ?? 0,
        ultimaActualizacion: generadoEn,
        expandible: obtenerDetalleBoletas(resumen)
    };
}

function normalizarMesesBoletasConDetalle(reporte: ReporteBoletasRecibidasAnualDto | ReporteBoletasTercerosEmitidasAnualDto | null, anioSeleccionado: number): MesBoletasHonorariosParseado[] {
    const resumenPorMes = new Map((reporte?.meses ?? []).map((mes) => [mes.mes, mes]));

    return Array.from({ length: 12 }, (_, indice) => {
        const numeroMes = indice + 1;
        const resumen = resumenPorMes.get(numeroMes);

        return crearMesBoletasConDetalle(numeroMes, nombreMes(numeroMes), reporte?.anio ?? anioSeleccionado, reporte?.generadoEn ?? '', resumen);
    });
}

function crearMesBoletasConDetalle(id: number, mes: string, anio: number, generadoEn: string, resumen?: ResumenMensualBoletasRecibidasDto | ResumenMensualBteEmitidasDto): MesBoletasHonorariosParseado {
    return {
        id,
        mes: resumen?.nombreMes || mes,
        anio,
        cantidadBoletas: resumen?.totales.totalBoletas ?? 0,
        vigentes: resumen?.totales.boletasVigentes ?? 0,
        anuladas: resumen?.totales.boletasAnuladas ?? 0,
        bruto: resumen?.totales.honorariosBrutos ?? 0,
        retenido: resumen?.totales.honorariosRetenidos ?? 0,
        pagado: resumen?.totales.honorariosPagados ?? 0,
        ultimaActualizacion: generadoEn,
        expandible: resumen?.boletas ?? []
    };
}

function obtenerDetalleBoletas(resumen?: ResumenPeriodoHonorarioDto): (BoletaRecibidaDto | BoletaTerceroEmitidaDto)[] {
    const detalle = (resumen as (ResumenPeriodoHonorarioDto & { boletas?: (BoletaRecibidaDto | BoletaTerceroEmitidaDto)[] }) | undefined)?.boletas;

    return Array.isArray(detalle) ? detalle : [];
}

export function parsearF50(respuesta: ReporteF50Dto): ReporteF50yF29Parseado {
    const mesesEncontrados: ReporteF50MesParseado[] = respuesta.periodosEncontrados.map((item) => ({
        numeroMes: item.mes,
        mes: item.periodo.periodo,
        estado: item.periodo.tieneDeclaracion ? 'Declaración encontrada\n' + item.periodo.folio : 'No hay declaraciones',
        detalle: item.periodo.detalles,
        noData: item.periodo.tieneDeclaracion ? '' : 'No existe un reporte para este mes.'
    }));

    const mesesNoEncontrados: ReporteF50MesParseado[] = respuesta.mesesNoEncontrados.map((item) => ({
        numeroMes: item.mes,
        mes: nombreMes(item.mes),
        estado: 'No hay declaraciones',
        detalle: [],
        noData: 'No existe un reporte para este mes.'
    }));

    return {
        rut: respuesta.rut,
        generadoEn: respuesta.periodosEncontrados.find((item) => item.generadoEn)?.generadoEn ?? null,
        meses: [...mesesEncontrados, ...mesesNoEncontrados].sort((a, b) => a.numeroMes - b.numeroMes)
    };
}

export function parsearF29(respuesta: ReporteF29Dto): ReporteF50yF29Parseado {
    const mesesEncontrados: ReporteF50MesParseado[] = respuesta.periodosEncontrados.map((item) => ({
        numeroMes: item.mes,
        mes: item.periodo.periodo,
        estado: item.periodo.tieneDeclaracion ? 'Declaración sin observaciones\n' + item.periodo.folio : 'No hay declaraciones',
        detalle: item.periodo.detalles,
        noData: item.periodo.tieneDeclaracion ? '' : 'No existe un reporte para este mes.'
    }));

    const mesesNoEncontrados: ReporteF50MesParseado[] = respuesta.mesesNoEncontrados.map((item) => ({
        numeroMes: item.mes,
        mes: nombreMes(item.mes),
        estado: 'No hay declaraciones',
        detalle: [],
        noData: 'No existe un reporte para este mes.'
    }));

    return {
        rut: respuesta.rut,
        generadoEn: respuesta.periodosEncontrados.find((item) => item.generadoEn)?.generadoEn ?? null,
        meses: [...mesesEncontrados, ...mesesNoEncontrados].sort((a, b) => a.numeroMes - b.numeroMes)
    };
}

export function parsearDatosTributarios(respuesta: ReporteDatosTributariosDto): ReporteDinamicoParseado {
    const tablas: Tabla[] = [];
    const data: Record<string, any[]> = {};

    respuesta.contenido.secciones.forEach((seccion, indiceSeccion) => {
        seccion.tablas.forEach((tablaApi, indiceTabla) => {
            const datosKey = `tributario_${indiceSeccion}_${indiceTabla}_${tablaApi.id}`;

            // Cantidad real de columnas.
            // Si vienen definidas, usamos esas.
            // Si no, las inferimos desde las filas.
            const cantidadColumnas = tablaApi.columnas.length > 0 ? tablaApi.columnas.length : Math.max(...tablaApi.filas.map((fila) => fila.length), 0);

            const columnas: Columnas[] = Array.from({ length: cantidadColumnas }, (_, indice) => ({
                nombre: `columna_${indice}`,
                titulo: tablaApi.columnas[indice] ?? ''
            }));

            const filas = tablaApi.filas.map((fila) => {
                const filaParseada: Record<string, string> = {};

                columnas.forEach((columna, indice) => {
                    filaParseada[columna.nombre] = fila[indice] ?? '';
                });

                return filaParseada;
            });

            tablas.push({
                datos: datosKey,

                titulo: tablaApi.titulo ? `${seccion.titulo} - ${tablaApi.titulo}` : seccion.titulo,

                subtitulo: seccion.textos.join('\n'),

                columnas
            });

            data[datosKey] = filas;
        });
    });

    return {
        rut: respuesta.rut,
        titulo: respuesta.titulo,
        generadoEn: respuesta.generadoEn,
        tablas,
        data
    };
}

export function parsearRegistroComprasVentas(respuesta: ReporteRegistroComprasVentasDto): ReporteRegistroComprasVentasParseado {
    const compras = (respuesta.meses ?? []).flatMap((mes) =>
        (mes.compra?.documentos ?? []).map((documento) => ({
            ...documento,
            mes: mes.mes,
            periodo: mes.periodo
        }))
    );

    const ventas = (respuesta.meses ?? []).flatMap((mes) =>
        (mes.venta?.documentos ?? []).map((documento) => ({
            ...documento,
            mes: mes.mes,
            periodo: mes.periodo
        }))
    );

    return {
        rut: respuesta.rut,
        anio: respuesta.anio,
        generadoEn: respuesta.generadoEn ?? null,

        compras,
        ventas
    };
}

export function parsearDeudasPropiedades(respuesta: ReporteDeudasPropiedadesDto) {
    return {
        ...respuesta,
        cuotasVencidas: respuesta.cuotasVencidas.map((cuota) => ({
            ...cuota,
            ...separarCuotaAnio(cuota.cuotas)
        }))
    };
}

function separarCuotaAnio(valor: string): { nroCuota: string; anio: string } {
    const separador = valor.indexOf('-');

    if (separador === -1) {
        return {
            nroCuota: valor.trim(),
            anio: ''
        };
    }

    return {
        nroCuota: valor.slice(0, separador).trim(),
        anio: valor.slice(separador + 1).trim()
    };
}

function agregarFilaTotal(documentos: DocumentoRegistroComprasVentasDto[]): DocumentoRegistroComprasVentasDto[] {
    return [
        ...documentos,
        {
            codigoDcv: null,
            codigoResumen: null,
            codigoTipoDocumento: null,
            nombreTipoDocumento: 'Total',
            tipoIngresoDocumento: null,
            detalleDisponible: null,
            montoExento: sumarColumna(documentos, 'montoExento'),
            montoNeto: sumarColumna(documentos, 'montoNeto'),
            montoIva: sumarColumna(documentos, 'montoIva'),
            montoIvaNoRecuperable: sumarColumna(documentos, 'montoIvaNoRecuperable'),
            montoIvaUsoComun: sumarColumna(documentos, 'montoIvaUsoComun'),
            operacion: null,
            fechaModificacion: null,
            montoTotal: sumarColumna(documentos, 'montoTotal'),
            estadoContable: null,
            totalDocumentos: sumarColumna(documentos, 'totalDocumentos'),
            totalRutEmisores: sumarColumna(documentos, 'totalRutEmisores')
        }
    ];
}

function sumarColumna(documentos: DocumentoRegistroComprasVentasDto[], columna: keyof DocumentoRegistroComprasVentasDto): number {
    return documentos.reduce((total, documento) => {
        const valor = documento[columna];

        return total + (typeof valor === 'number' ? valor : 0);
    }, 0);
}

export function parsearComunicaciones(respuesta: ReporteComunicacionesDto): ReporteComunicacionesParseado {
    return {
        rut: respuesta.rut,
        generadoEn: respuesta.generadoEn ?? null,

        notificaciones: respuesta.notificaciones?.items ?? [],

        cartasCorreosInformativos: respuesta.cartasCorreosInformativos?.items ?? [],
        anotacionesVigentes: respuesta.anotacionesVigentes

    };
}

export function parsearMultas(respuesta: ReporteMultasDto): ReporteMultasParseado {
    const multasFormulario21 = respuesta.formulario21 ?? [];
    const multasFormulario45 = respuesta.formulario45 ?? [];
    const formulario21 = multasFormulario21.map(parsearMulta);
    const formulario45 = multasFormulario45.map(parsearMulta);

    return {
        rut: respuesta.rut,
        generadoEn: respuesta.generadoEn ?? null,

        formulario21,
        formulario45,

        graficoFormulario21: crearDatosGrafico(multasFormulario21),
        graficoFormulario45: crearDatosGrafico(multasFormulario45)
    };
}

function parsearMulta(multa: MultaDto): MultaParseada {
    return {
        ...multa,
        totalPagar: obtenerMonto(multa.totalPagar)
    };
}

function crearDatosGrafico(multas: MultaDto[]): ChartData<'line', number[], string> {
    const totalesPorMes = new Map<number, number>();

    for (const multa of multas) {
        const fechaEmision = obtenerFechaEmision(multa.fechaEmision);

        const totalPagar = obtenerMonto(multa.totalPagar);

        if (!fechaEmision || totalPagar === null) {
            continue;
        }

        const mes = fechaEmision.getFullYear() * 12 + fechaEmision.getMonth();

        totalesPorMes.set(mes, (totalesPorMes.get(mes) ?? 0) + totalPagar);
    }

    const mesesConMultas = [...totalesPorMes.entries()]
        .filter(([, total]) => total > 0)
        .map(([mes]) => mes)
        .sort((a, b) => a - b);

    const labels: string[] = [];
    const acumulados: number[] = [];

    let acumulado = 0;

    for (const mes of mesesConMultas) {
        const anio = Math.floor(mes / 12);
        const numeroMes = (mes % 12) + 1;

        acumulado += totalesPorMes.get(mes) ?? 0;

        labels.push(`${numeroMes.toString().padStart(2, '0')}/${anio}`);

        acumulados.push(acumulado);
    }

    return {
        labels,

        datasets: [
            {
                label: 'Total acumulado a pagar',
                data: acumulados,
                stepped: false,
                fill: false,
                pointRadius: 3,
                pointHoverRadius: 5
            }
        ]
    };
}

function obtenerFechaEmision(valor: string | null | undefined): Date | null {
    if (!valor) {
        return null;
    }

    const fecha = valor.trim();
    const formatoDiaPrimero = /^(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{4})(?:\D|$)/.exec(fecha);
    const formatoAnioPrimero = /^(\d{4})[\/.\-](\d{1,2})[\/.\-](\d{1,2})(?:\D|$)/.exec(fecha);

    if (formatoDiaPrimero) {
        return crearFechaValida(Number(formatoDiaPrimero[3]), Number(formatoDiaPrimero[2]), Number(formatoDiaPrimero[1]));
    }

    if (formatoAnioPrimero) {
        return crearFechaValida(Number(formatoAnioPrimero[1]), Number(formatoAnioPrimero[2]), Number(formatoAnioPrimero[3]));
    }

    return null;
}

function crearFechaValida(anio: number, mes: number, dia: number): Date | null {
    const fecha = new Date(anio, mes - 1, dia);

    return fecha.getFullYear() === anio && fecha.getMonth() === mes - 1 && fecha.getDate() === dia ? fecha : null;
}

function obtenerMonto(valor: string | null | undefined): number | null {
    if (!valor) {
        return null;
    }

    const monto = valor.trim().replace(/[^\d,.-]/g, '');

    if (!monto) {
        return null;
    }

    const signo = monto.startsWith('-') ? -1 : 1;
    const valorSinSigno = monto.replace(/-/g, '');
    const ultimaComa = valorSinSigno.lastIndexOf(',');
    const ultimoPunto = valorSinSigno.lastIndexOf('.');
    let valorNormalizado: string;

    if (ultimaComa >= 0 && ultimoPunto >= 0) {
        const separadorDecimal = ultimaComa > ultimoPunto ? ',' : '.';
        const separadorMiles = separadorDecimal === ',' ? /\./g : /,/g;
        valorNormalizado = valorSinSigno.replace(separadorMiles, '').replace(separadorDecimal, '.');
    } else {
        const separador = ultimaComa >= 0 ? ',' : ultimoPunto >= 0 ? '.' : null;

        if (!separador) {
            valorNormalizado = valorSinSigno;
        } else {
            const partes = valorSinSigno.split(separador);
            const usaSeparadorDeMiles = partes.length > 2 || (partes.length === 2 && partes[1].length === 3);
            valorNormalizado = usaSeparadorDeMiles ? partes.join('') : `${partes[0]}.${partes[1]}`;
        }
    }

    const numero = Number(valorNormalizado) * signo;

    return Number.isFinite(numero) ? numero : null;
}

export function parsearDevolucionesIva(respuesta: ReporteDevolucionesIvaDto) {
    return {
        ...respuesta,

        periodos: respuesta.periodos
            .map((periodo) => ({
                ...periodo,

                periodo: `${periodo.anio}-${periodo.mes
                    .toString()
                    .padStart(2, '0')}`
            }))
            .sort((a, b) =>
                a.anio !== b.anio
                    ? a.anio - b.anio
                    : a.mes - b.mes
            )
    };
}


export function parsearF22(dto: ReporteF22Dto) {
    return {
        ...dto,

        situacionRentaActual: dto.situacionRentaActual
            ? [dto.situacionRentaActual]
            : [],

        historial: dto.historial
            ? [dto.historial]
            : []
    };
}
